import { Fragment, useState, useCallback, useContext, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container, Row, Col, Image, Button, Form, Alert, Stack, ProgressBar, Modal, Spinner } from "react-bootstrap";
import useAxios from "axios-hooks";
import BrandContext from "./BrandContext";
import { LinkContainer } from "react-router-bootstrap";
import { PRPreview } from "./PRPreview";

export default function Promote(props) {
    const theme = useContext(BrandContext);
    const nav = useNavigate();
    const params = useParams();
    const [{ data: currentPR, loading: loadingPR, error: prError }, refetchPR] = useAxios({
        method: 'get',
        url: '/pr/get',
        params: {
            id: params.prId
        },
    });
    const [showModal, setShowModal] = useState(false);
    const handleCloseModal = () => {
        setShowModal(false)
    };
    useEffect(() => {
        if (currentPR) {
            // Content wasn't completed, go back to content
            if (currentPR && !currentPR?.prTitle) {
                nav('../content');
                return;
            }
            const status = currentPR?.status;
            switch (status) {
                case 'paid':
                    nav('../checkout');
                    break;
                case 'submitted':
                    nav('../submitted');
                    break;
                default:
                    setShowModal(true)
                    let curSelectedRelatedBrands = {};
                    (currentPR?.additionalBrands || []).forEach(b => curSelectedRelatedBrands[b] = true);
                    setSelectedRelatedBrands(curSelectedRelatedBrands)
                    break;
            }
        }
    }, [currentPR]);


    const [selectedDiscountBrand, setSelectedDiscountBrand] = useState('');
    const [selectedDiscountCode, setSelectedDiscountCode] = useState('');
    const [discountCodeError, setDiscountCodeError] = useState('');

    const [showPaidModal, setShowPaidModal] = useState(false);
    const handleClosePaidModal = () => {
        setSelectedDiscountBrand('')
        setSelectedDiscountCode('')
        setDiscountCodeError('')
        setShowPaidModal(false)
    };
    const handleShowPaidModal = () => setShowPaidModal(true);

    const [selectedRelatedBrands, setSelectedRelatedBrands] = useState({});
    const selectBrand = useCallback((evt) => {
        setSelectedRelatedBrands((prevState) => {
            const updatedValues = {};
            updatedValues[evt.target.id] = evt.target.checked;
            return { ...prevState, ...updatedValues };
        });
    }, [selectedRelatedBrands]);

    const [discountCodes, setDiscountCodes] = useState({});
    const addDiscountCode = useCallback((discountCode) => {
        setDiscountCodes((prevState) => {
            const updatedValues = {};
            updatedValues[discountCode.brandId] = discountCode;
            return { ...prevState, ...updatedValues };
        });
    }, [discountCodes]);

    const getTotalAudience = function () {
        let totalAudience = theme?.marketing?.audience || 0;
        for (const b in selectedRelatedBrands) {
            if (selectedRelatedBrands[b]) totalAudience += theme?.relatedBrands[b]?.marketing?.audience || 0;
        }
        return totalAudience;
    }
    const getAvailableAudience = function () {
        let availableAudience = theme?.marketing?.audience || 0;
        for (const b in theme?.relatedBrands) {
            availableAudience += theme?.relatedBrands[b]?.marketing?.audience || 0;
        }
        return availableAudience;
    }

    const getTotalEditors = function () {
        let editors = theme?.editorCount || 0;
        for (const b in selectedRelatedBrands) {
            if (selectedRelatedBrands[b]) editors += theme?.relatedBrands[b]?.editorCount || 0;
        }
        return editors;
    }
    const getAvailableEditors = function () {
        let editors = theme?.editorCount || 0;
        for (const b in theme?.relatedBrands) {
            editors += theme?.relatedBrands[b]?.editorCount || 0;
        }
        return editors;
    }
    const getTotalCost = function () {
        // Calc primary brand cost
        let cost = discountCodes[theme?.brandId]?.discountPrice ?? theme?.price ?? 0;
        // Calc related brand costs
        for (const b in selectedRelatedBrands) {
            if (selectedRelatedBrands[b]) {
                cost += discountCodes[theme?.relatedBrands[b]?.brandId]?.discountPrice ?? theme?.relatedBrands[b]?.price ?? 0;
            }
        }
        return cost;
    }

    const [
        { loading: validatingDiscount },
        executeValidateDiscount
    ] = useAxios(
        {
            url: '/pr/validate-discount-code',
            method: 'POST'
        },
        { manual: true }
    )

    const [
        { data: draft, loading: working, error: createError },
        executePut
    ] = useAxios(
        {
            url: '/pr/promote',
            method: 'POST'
        },
        { manual: true }
    )

    async function validateDiscount(brandId, discountCode) {
        try {
            const { data: validationResult } = await executeValidateDiscount({
                data: {
                    id: params.prId,
                    brandId: brandId,
                    discountCode: discountCode
                }
            })
            addDiscountCode(validationResult)
            handleClosePaidModal()
        }
        catch (error) {
            setDiscountCodeError(error.response?.data?.error || 'Error validating code')
        }
    }

    function promote() {
        executePut({
            data: {
                id: params.prId,
                additionalBrands: Object.keys(selectedRelatedBrands).filter(b => selectedRelatedBrands[b]),
                discountCodes: discountCodes
            }
        })
            .then((r) => {
                nav('../checkout')
            })
            .catch((e) => {
                console.error(e)
            })
            .finally(() => {

            })
    }

    return (
        <Fragment>
            <Container fluid="md" style={{ paddingTop: "50px", paddingBottom: "50px" }}>
                <Row className="justify-content-center">
                    <Col sm={{ span: true, order: 'first' }} xs={{ span: 12, order: 'last' }} style={{ padding: "15px" }}>
                        <p style={{ fontSize: "14px" }}><span className="sr-light">Create</span> <span className="fw-bold text-swiftrev">&gt;</span> <span className="fw-bold">Promote</span> <span className="fw-bold text-swiftrev">&gt;</span> <span className="sr-light">Checkout</span></p>
                        <p className="fw-bold" style={{ fontSize: "24px", lineHeight: "26px" }}>Expand your impact</p>
                        <p>{theme?.name} is part of a larger organization offering a unique opportunity to bring your announcement to a larger audience. Add audiences below to maximize your reach and impact.</p>
                    </Col>
                    <Col sm={{ span: 'auto', order: 'last' }} xs={{ span: 12, order: 'first' }} style={{ padding: "15px", display: "grid", alignContent: "start", justifyContent: "start" }}>
                        <Image style={{ height: "50px" }} fluid src={theme?.marketing?.logo}></Image>
                    </Col>
                </Row>
                <Row className="justify-content-center">
                    <Col md="8" style={{ padding: "15px" }}>
                        <Alert className="bg-swiftrev"><span className="fw-bold">{theme?.marketing?.divisionName}</span></Alert>
                        <Row >
                            <Col md="6">
                                <h5 className="fw-bold">Expand Audience Reach</h5>
                                <h4 className="fw-bold">{getTotalAudience()?.toLocaleString()}</h4>
                                <p>Current audience reach from your selected communities.</p>
                                <ProgressBar className="progress-swiftrev" now={100 * getTotalAudience() / getAvailableAudience()} />
                            </Col>
                            <Col md="6">
                                <h5 className="fw-bold">Notify Thought Leaders</h5>
                                <h4 className="fw-bold">{getTotalEditors()?.toLocaleString()}</h4>
                                <p style={{ height: "48px" }}>Editors that will see your announcement.</p>
                                <ProgressBar className="progress-swiftrev" now={100 * getTotalEditors() / getAvailableEditors()} />
                            </Col>
                        </Row>
                        <Row style={{ paddingTop: "25px" }}>
                            <div className="bg-light" style={{ padding: "20px", marginBottom: "20px" }}>
                                <Form.Check className="check-swiftrev">
                                    <Form.Check.Input checked readOnly disabled type="checkbox" />
                                    <Form.Check.Label style={{ opacity: "1" }}>{theme?.name}</Form.Check.Label>
                                </Form.Check>
                                <div style={{ paddingLeft: "24px" }}>
                                    <p>{theme?.marketing?.description}</p>
                                    <Row >
                                        <Col md="6">
                                            <div className="fw-bold">Monthly Audience Reach</div>
                                            <p>{theme?.marketing?.audience?.toLocaleString()}</p>
                                        </Col>
                                        <Col md="6">
                                            <div className="fw-bold"># of Editors</div>
                                            <p>{theme?.editorCount}</p>
                                        </Col>
                                    </Row>
                                    <Row >
                                        <Col md="6">
                                            <div className="fw-bold">Top Industries Served</div>
                                            {theme?.marketing?.industries?.map((industry, indx) => {
                                                return <div key={'indx-' + indx}>- {industry}</div>
                                            })}
                                        </Col>
                                        <Col md="6">
                                            <div className="fw-bold">Top Job Functions</div>
                                            {theme?.marketing?.jobs?.map((job, indx) => {
                                                return <div key={'indx-' + indx}>- {job}</div>
                                            })}
                                        </Col>
                                    </Row>
                                </div>
                            </div>
                            {Object.keys(theme?.relatedBrands || {}).map(i => {
                                return <div key={theme?.relatedBrands[i].brandId} id={theme?.relatedBrands[i].brandId} className="bg-light" style={{ padding: "20px", marginBottom: "20px" }}>
                                    <Form.Check checked={selectedRelatedBrands[theme?.relatedBrands[i]?.brandId]}
                                        id={theme?.relatedBrands[i].brandId} onChange={selectBrand}
                                        className="check-swiftrev" label={theme?.relatedBrands[i].name} />
                                    <div style={{ paddingLeft: "24px" }}>
                                        <p>{theme?.relatedBrands[i].marketing?.description}</p>
                                        <Row >
                                            <Col md="6">
                                                <div className="fw-bold">Monthly Audience Reach</div>
                                                <p>{theme?.relatedBrands[i].marketing?.audience?.toLocaleString()}</p>
                                            </Col>
                                            <Col md="6">
                                                <div className="fw-bold"># of Editors</div>
                                                <p>{theme?.relatedBrands[i].editorCount}</p>
                                            </Col>
                                        </Row>
                                        <Row >
                                            <Col md="6">
                                                <div className="fw-bold">Top Industries Served</div>
                                                {theme?.relatedBrands[i].marketing?.industries?.map((industry, indx) => {
                                                    return <div key={'indx-' + indx}>- {industry}</div>
                                                })}
                                            </Col>
                                            <Col md="6">
                                                <div className="fw-bold">Top Job Functions</div>
                                                {theme?.relatedBrands[i].marketing?.jobs?.map((job, indx) => {
                                                    return <div key={'indx-' + indx}>- {job}</div>
                                                })}
                                            </Col>
                                        </Row>
                                    </div>
                                </div>
                            })}
                        </Row>
                    </Col>
                    <Col md="4" style={{ paddingTop: "15px", paddingLeft: "25px", paddingRight: "25px" }}>
                        <h4 className="fw-bold">Purchase Summary</h4>
                        <Stack direction="horizontal">
                            <div>{theme?.name}</div>
                            {
                                !discountCodes[theme?.brandId] &&
                                <div className="ms-auto">{theme?.priceSymbol}{theme?.price} {theme?.priceAbbr}</div>
                            }
                            {
                                discountCodes[theme?.brandId] &&
                                <div className="ms-auto">
                                    <span style={{ marginRight: "20px" }} className="text-decoration-line-through">{theme?.priceSymbol}{theme?.price} {theme?.priceAbbr}</span>
                                    <span>{theme?.priceSymbol}{discountCodes[theme?.brandId].discountPrice} {theme?.priceAbbr}</span>
                                </div>
                            }
                        </Stack>
                        {Object.keys(selectedRelatedBrands).map((i, indx) => {
                            if (selectedRelatedBrands[i]) {
                                return <Stack key={'indx-' + indx} direction="horizontal">
                                    <div>{theme?.relatedBrands[i]?.name}</div>
                                    {
                                        !discountCodes[theme?.relatedBrands[i]?.brandId] &&
                                        <div className="ms-auto">{theme?.priceSymbol}{theme?.relatedBrands[i]?.price} {theme?.priceAbbr}</div>
                                    }
                                    {
                                        discountCodes[theme?.relatedBrands[i]?.brandId] &&
                                        <div className="ms-auto">
                                            <span style={{ marginRight: "20px" }} className="text-decoration-line-through">{theme?.priceSymbol}{theme?.relatedBrands[i]?.price} {theme?.priceAbbr}</span>
                                            <span>{theme?.priceSymbol}{discountCodes[theme?.relatedBrands[i]?.brandId].discountPrice} {theme?.priceAbbr}</span>
                                        </div>
                                    }
                                </Stack>
                            }
                            else return <div key={'indx-' + indx}></div>
                        })}
                        <hr />
                        <Stack direction="horizontal">
                            <div className="fw-bold">Total</div>
                            <div className="ms-auto fw-bold">{theme?.priceSymbol}{getTotalCost()} {theme?.priceAbbr}</div>
                        </Stack>
                        <Button style={{ marginTop: "15px" }} onClick={() => handleShowPaidModal()}
                            size="sm" variant="outline-swiftrev">Apply Discount Code</Button>
                    </Col>
                </Row>
                <Stack direction="horizontal" gap={3}>
                    <Button onClick={() => promote()} disabled={working} size="lg" variant="swiftrev">
                        {getTotalCost() === 0 ? 'Purchase' : 'Next'}
                    </Button>
                    <LinkContainer to="../content">
                        <Button size="lg" variant="none">Back</Button>
                    </LinkContainer>
                </Stack>
            </Container>
            <Modal show={showModal} onHide={() => handleCloseModal()} size="xl" centered backdrop='static'>
                <Modal.Header><Modal.Title>Please Confirm Press Release Content</Modal.Title></Modal.Header>
                <Modal.Body>
                    <PRPreview pr={currentPR} />
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={() => nav('../content')} variant="outline-swiftrev">Modify</Button>
                    <Button onClick={() => handleCloseModal()} variant="swiftrev">Confirm</Button>
                </Modal.Footer>
            </Modal>
            <Modal show={showPaidModal} onHide={handleClosePaidModal} size="md" centered>
                <Modal.Body>
                    <Stack gap={3}>
                        <Form.Group>
                            <Form.Label>Select Brand</Form.Label>
                            <Form.Select required onChange={event => setSelectedDiscountBrand(event.target.value)}>
                                <option key='' value=''></option>
                                <option key={theme?.brandId} value={theme?.brandId}>{theme?.name}</option>
                                {Object.keys(selectedRelatedBrands || {}).map(i => {
                                    return (<option value={theme?.relatedBrands[i].brandId} key={theme?.relatedBrands[i].brandId}>{theme?.relatedBrands[i].name}</option>)
                                })}
                            </Form.Select>
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>Discount Code</Form.Label>
                            <Form.Control required type="text"
                                onChange={event => setSelectedDiscountCode(event.target.value)} />
                        </Form.Group>
                        <div>
                            <Button onClick={() => validateDiscount(selectedDiscountBrand, selectedDiscountCode)} size="sm" variant="swiftrev"
                                disabled={validatingDiscount || !selectedDiscountBrand || !selectedDiscountCode}>
                                <Spinner hidden={!validatingDiscount} style={{ marginRight: "10px" }}
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"></Spinner>
                                Apply
                            </Button>
                        </div>
                        <div className="text-danger" hidden={!discountCodeError}>{discountCodeError}</div>
                    </Stack>
                </Modal.Body>
            </Modal>
        </Fragment>
    );
}